import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiCheck, FiX, FiBell, FiClock } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { reminderService } from '../services/reminderService';
import { Reminder, ReminderFormData } from '../types';
import './Reminders.css';

const Reminders: React.FC = () => {
  const { t } = useTranslation();
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editingReminder, setEditingReminder] = useState<Reminder | null>(null);
  const [formData, setFormData] = useState<ReminderFormData>({
    title: '',
    description: '',
    reminderDate: ''
  });

  useEffect(() => {
    fetchReminders();
  }, []);

  const fetchReminders = async (): Promise<void> => {
    try {
      const reminders = await reminderService.getReminders();
      setReminders(reminders);
      setLoading(false);
    } catch (error) {
      console.error('Hatırlatıcılar yüklenemedi:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    
    try {
      if (editingReminder) {
        await reminderService.updateReminder(editingReminder._id, formData);
      } else {
        await reminderService.createReminder(formData);
      }
      
      fetchReminders();
      closeModal();
    } catch (error) {
      console.error('Hatırlatıcı kaydedilemedi:', error);
    }
  };

  const handleDelete = async (id: string): Promise<void> => {
    if (window.confirm(t('reminders.deleteConfirm'))) {
      try {
        await reminderService.deleteReminder(id);
        fetchReminders();
      } catch (error) {
        console.error('Hatırlatıcı silinemedi:', error);
      }
    }
  };

  const openModal = (reminder: Reminder | null = null): void => {
    if (reminder) {
      setEditingReminder(reminder);
      setFormData({
        title: reminder.title,
        description: reminder.description || '',
        reminderDate: reminder.reminderDate.split('T')[0] + 'T' + 
                      reminder.reminderDate.split('T')[1].substring(0, 5)
      });
    } else {
      setEditingReminder(null);
      setFormData({
        title: '',
        description: '',
        reminderDate: ''
      });
    }
    setShowModal(true);
  };

  const closeModal = (): void => {
    setShowModal(false);
    setEditingReminder(null);
  };

  const isUpcoming = (date: string): boolean => {
    return new Date(date) > new Date();
  };

  if (loading) {
    return (
      <div className="page-loading">
        <div className="spinner"></div>
        <p>Yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="reminders-page fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('reminders.title')}</h1>
          <p className="page-subtitle">{t('reminders.subtitle')}</p>
        </div>
        <button className="btn btn-primary" onClick={() => openModal()}>
          <FiPlus /> {t('reminders.addReminder')}
        </button>
      </div>

      {reminders.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🔔</div>
          <h3 className="empty-state-title">{t('reminders.noReminders')}</h3>
          <p className="empty-state-text">{t('reminders.createFirst')}</p>
          <button className="btn btn-primary" onClick={() => openModal()}>
            <FiPlus /> {t('reminders.addReminder')}
          </button>
        </div>
      ) : (
        <div className="reminders-list">
          {reminders.map(reminder => (
            <div 
              key={reminder._id} 
              className={`reminder-card ${!isUpcoming(reminder.reminderDate) ? 'past' : ''}`}
            >
              <div className="reminder-icon">
                {isUpcoming(reminder.reminderDate) ? <FiBell /> : <FiClock />}
              </div>
              
              <div className="reminder-content">
                <div className="reminder-header">
                  <h3 className="reminder-title">{reminder.title}</h3>
                </div>
                
                {reminder.description && (
                  <p className="reminder-description">{reminder.description}</p>
                )}
                
                <div className="reminder-date">
                  📅 {new Date(reminder.reminderDate).toLocaleString('tr-TR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
              
              <div className="reminder-actions">
                <button 
                  className="btn-icon btn-edit" 
                  onClick={() => openModal(reminder)}
                  title={t('common.edit')}
                >
                  <FiEdit2 />
                </button>
                <button 
                  className="btn-icon btn-delete" 
                  onClick={() => handleDelete(reminder._id)}
                  title={t('common.delete')}
                >
                  <FiTrash2 />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingReminder ? t('reminders.editReminder') : t('reminders.newReminder')}</h2>
              <button className="modal-close" onClick={closeModal}>
                <FiX />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="input-group">
                <label className="input-label">Başlık *</label>
                <input
                  type="text"
                  className="input-field"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                />
              </div>

              <div className="input-group">
                <label className="input-label">Açıklama</label>
                <textarea
                  className="textarea-field"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                />
              </div>

              <div className="input-group">
                <label className="input-label">Tarih ve Saat *</label>
                <input
                  type="datetime-local"
                  className="input-field"
                  value={formData.reminderDate}
                  onChange={(e) => setFormData({...formData, reminderDate: e.target.value})}
                  required
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={closeModal}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn-primary">
                  <FiCheck /> {editingReminder ? t('common.update') : t('common.create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reminders;

